/**
 * Sales pricing calculator & sales-invoice engine (PE-2143).
 *
 * Shapes mirror order-service's sales DTOs/Prisma models exactly — osprey-api's
 * `/sales/*` controllers take `@Body() body: unknown` and proxy straight through,
 * so order-service is the only real contract authority here (verified against
 * `order-service/src/domain/sales/**` and `prisma/sales/schema/sales.model.prisma`).
 *
 * MONEY: every `*Minor` field is an integer in minor units (kobo). Percentages are
 * FRACTIONS in 0..1 (0.65 = 65%), not whole percents — the backend validates
 * `@Min(0) @Max(1)`, so sending 65 is a 400.
 *
 * ACCESS: all admin routes are TCS-staff-only (CollectiveOnlyGuard) plus the
 * watchman `SALES_PRICING` resource. See permissions.ts SALES_PRICING_*.
 */

/** Whether a feature is part of the core build or a chargeable add-on. */
export const SalesFeatureCategory = {
  CORE: 'CORE',
  ADD_ON: 'ADD_ON',
} as const;
export type SalesFeatureCategory =
  (typeof SalesFeatureCategory)[keyof typeof SalesFeatureCategory];

export const BusinessImpact = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
} as const;
export type BusinessImpact = (typeof BusinessImpact)[keyof typeof BusinessImpact];

/** How the deal is priced: a one-time build fee, or commission on sales. */
export const SalesCommercialModel = {
  ONE_TIME: 'ONE_TIME',
  COMMISSION_ON_SALES: 'COMMISSION_ON_SALES',
} as const;
export type SalesCommercialModel =
  (typeof SalesCommercialModel)[keyof typeof SalesCommercialModel];

/** Which of the two computed price points the prospect is being quoted. */
export const SalesPriceTier = {
  MINIMUM: 'MINIMUM',
  IDEAL: 'IDEAL',
} as const;
export type SalesPriceTier = (typeof SalesPriceTier)[keyof typeof SalesPriceTier];

export const SalesInvoiceStatus = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  VIEWED: 'VIEWED',
  SIGNED: 'SIGNED',
  PAID: 'PAID',
  OVERDUE: 'OVERDUE',
  CANCELLED: 'CANCELLED',
  VOID: 'VOID',
  EXPIRED: 'EXPIRED',
} as const;
export type SalesInvoiceStatus =
  (typeof SalesInvoiceStatus)[keyof typeof SalesInvoiceStatus];

export const SalesAcceptanceMethod = {
  E_SIGNATURE: 'E_SIGNATURE',
  EXPLICIT_ACCEPT: 'EXPLICIT_ACCEPT',
} as const;
export type SalesAcceptanceMethod =
  (typeof SalesAcceptanceMethod)[keyof typeof SalesAcceptanceMethod];

export const FEATURE_CATEGORY_LABELS: Record<SalesFeatureCategory, string> = {
  CORE: 'Core',
  ADD_ON: 'Add-on',
};

export const BUSINESS_IMPACT_LABELS: Record<BusinessImpact, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const COMMERCIAL_MODEL_LABELS: Record<SalesCommercialModel, string> = {
  ONE_TIME: 'One-time fee',
  COMMISSION_ON_SALES: 'Commission on sales',
};

export const PRICE_TIER_LABELS: Record<SalesPriceTier, string> = {
  MINIMUM: 'Minimum',
  IDEAL: 'Ideal',
};

/** A catalogue feature the calculator prices. */
export interface SalesFeature {
  id: string;
  name: string;
  category: SalesFeatureCategory;
  /** Build cost in kobo. */
  developmentCostMinor: number;
  businessImpact: BusinessImpact;
  /** Recurring annual platform fee attributable to this feature, in kobo. */
  annualPlatformFeeMinor: number;
  isActive: boolean;
  displayOrder?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSalesFeaturePayload {
  name: string;
  category: SalesFeatureCategory;
  developmentCostMinor: number;
  businessImpact: BusinessImpact;
  annualPlatformFeeMinor: number;
  displayOrder?: number;
}
export type UpdateSalesFeaturePayload = Partial<CreateSalesFeaturePayload> & {
  isActive?: boolean;
};

export interface SalesFeatureQuery {
  category?: SalesFeatureCategory;
  isActive?: boolean;
}

/** The computed price points. Returned by preview and embedded in a selection. */
export interface SalesCalculatorResult {
  coreSubtotalMinMinor: number;
  coreSubtotalIdealMinor: number;
  corePlatformFeeMinor: number;
  addOnSubtotalMinMinor: number;
  addOnSubtotalIdealMinor: number;
  addOnPlatformFeeMinor: number;
  totalPlatformFeeMinor: number;
  totalPayableMinMinor: number;
  totalPayableIdealMinor: number;
}

export interface SalesCalculatorPreviewPayload {
  /** Add-on feature ids to include on top of the core set. Backend requires UUIDs. */
  addOnFeatureIds: string[];
  commercialModel: SalesCommercialModel;
  chosenTier?: SalesPriceTier;
  /** Fraction 0..1. Only meaningful for COMMISSION_ON_SALES. */
  commissionPercentage?: number;
}

export type PersistSelectionPayload = SalesCalculatorPreviewPayload & {
  prospectName?: string;
};

/** A persisted calculator run — the frozen source an invoice is generated from. */
export interface SalesCalculatorSelection extends SalesCalculatorResult {
  id: string;
  prospectName?: string | null;
  commercialModel: SalesCommercialModel;
  chosenTier?: SalesPriceTier | null;
  commissionPercentage?: number | null;
  createdById?: string | null;
  createdAt: string;
}

export interface SalesInvoiceLineItem {
  id?: string;
  featureId?: string | null;
  description: string;
  quantity: number;
  unitAmountMinor: number;
  totalAmountMinor: number;
}

/** Line-item shape on the way IN (no id; server assigns). */
export interface SalesInvoiceLineItemInput {
  featureId?: string;
  description: string;
  quantity: number;
  unitAmountMinor: number;
  totalAmountMinor: number;
}

export interface SalesInvoiceTermsSection {
  key: string;
  title: string;
  body: string;
}

export interface SalesInvoice {
  id: string;
  invoiceNumber: string;
  selectionId: string;
  status: SalesInvoiceStatus;
  prospectName: string;
  prospectCompany?: string | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  currency: string;
  subtotalMinor: number;
  annualPlatformFeeMinor: number;
  creditEnabled: boolean;
  creditLabel?: string | null;
  creditMinor: number;
  totalPayableMinor: number;
  commissionAlternativePercentage?: number | null;
  issuedAt?: string | null;
  /** After this instant the quote is stale; backend flips status to EXPIRED. */
  priceExpiresAt?: string | null;
  dueDate?: string | null;
  paidAt?: string | null;
  sentToCustomerAt?: string | null;
  viewedAt?: string | null;
  accessTokenRevoked?: boolean;
  lineItems?: SalesInvoiceLineItem[];
  termsSnapshot?: SalesInvoiceTermsSection[];
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSalesInvoicePayload {
  /** The persisted calculator selection this invoice is frozen from. Required. */
  selectionId: string;
  prospectName: string;
  prospectCompany?: string;
  recipientEmail?: string;
  recipientName?: string;
  creditEnabled?: boolean;
  creditLabel?: string;
  creditMinor?: number;
}

export interface UpdateSalesInvoicePayload {
  /** Backend enforces ArrayMinSize(1) when present — never send []. */
  lineItems?: SalesInvoiceLineItemInput[];
  annualPlatformFeeMinor?: number;
  creditEnabled?: boolean;
  creditLabel?: string;
  creditMinor?: number;
  prospectName?: string;
  prospectCompany?: string;
  recipientEmail?: string;
  recipientName?: string;
  /** Backend enforces ArrayMinSize(1) when present — never send []. */
  termsOverride?: SalesInvoiceTermsSection[];
}

export interface SendSalesInvoicePayload {
  recipientEmail: string;
  recipientName?: string;
}

/** Only CANCELLED and VOID are settable from the admin UI (backend @IsIn). */
export type SettableInvoiceStatus = 'CANCELLED' | 'VOID';

export interface SalesPricingAssumptions {
  id: string;
  exchangeRateNgnPerUsd: number;
  /** Kobo per hour. */
  hourlySupportRateMinor: number;
  /** All margin/commission fields are fractions 0..1. */
  minMarginLowPercentage: number;
  idealMarginLowPercentage: number;
  minMarginMediumPercentage: number;
  idealMarginMediumPercentage: number;
  minMarginHighPercentage: number;
  idealMarginHighPercentage: number;
  standardCommissionPercentage: number;
  platformFeeMarginPercentage: number;
  updatedById?: string | null;
  updatedAt?: string;
}

export type UpdateSalesPricingAssumptionsPayload = Partial<
  Omit<SalesPricingAssumptions, 'id' | 'updatedById' | 'updatedAt'>
>;

export interface SalesInvoiceTermsTemplate {
  id: string;
  version: number;
  sections: SalesInvoiceTermsSection[];
  updatedById?: string | null;
  updatedAt?: string;
}

export interface UpdateSalesTermsTemplatePayload {
  sections: SalesInvoiceTermsSection[];
}
